/**
 * The person filter (spec M1-B4 §8.5, `[ui:library/table-09-person-filter.png]`).
 *
 * Measured x 605 -> 1040, y 237 -> 452: `Filter this board by person ⓘ` + `Save to this view`,
 * a `Search` field and a grid of member avatars.
 *
 * **P-089 (round 10e):** monday's first row is **assigned to me** — the commonest filter on the
 * toolbar, which took a search-and-click here because the pane rendered `boards.members` and
 * nothing else. It writes the same `person`-column filter as any other pick, with the signed-in
 * user's id (`payload.me`), and is ticked when that user is the only one selected. The pane was
 * never photographed, so the row's exact label and position are monday's product and not a
 * capture in this repo — **q509**. It is a shortcut that filters on **every**
 * people-like column of the board with `any_of [selected ids]`, OR-ed together (ASSUMED, Q171),
 * and it is stored as an ordinary `FilterGroup` so "Save to this view" and `/v2` both work.
 */
import { useState } from 'react';
import { getColumnUi } from '../../columns/registry';
import { PersonAvatar } from '../../columns/primitives';
import { useTable, useTableActions } from '../context';
import type { FilterGroup } from '../settings';
import { Pane, PANE_BOX } from './Pane';
import { useSaveLabel } from './useSaveLabel';

const PEOPLE_LIKE = new Set(['people', 'person', 'team', 'item_assignees']);

export function PersonFilterPane() {
  const payload = useTable((state) => state.payload);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);

  const me = payload?.me === undefined ? null : String(payload.me);
  const assignedToMe = me !== null && picked.length === 1 && picked[0] === me;

  const people = (payload?.people ?? []).filter((person) =>
    person.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const personColumns = (payload?.columns ?? []).filter(
    (column) => PEOPLE_LIKE.has(column.type) && getColumnUi(column.type).groupable,
  );

  const apply = (ids: string[]) => {
    setPicked(ids);
    if (ids.length === 0) {
      actions.setEffective({ filter: null });
      return;
    }
    const filter: FilterGroup = {
      operator: 'or',
      rules: personColumns.map((column) => ({
        columnId: column.id,
        operator: 'any_of' as const,
        compareValue: ids.map(Number),
      })),
      groups: [],
    };
    actions.setEffective({ filter });
  };

  return (
    <Pane
      testId="person-filter-pane"
      title="Filter this board by person"
      {...PANE_BOX.person}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      onClose={() => actions.openPane(null)}
    >
      <input
        className="ez-board-pane__search"
        type="search"
        aria-label="Search"
        placeholder="Search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {me === null ? null : (
        <button
          type="button"
          className="ez-person-filter__me"
          aria-pressed={assignedToMe}
          data-testid="person-filter-assigned-to-me"
          onClick={() => apply(assignedToMe ? [] : [me])}
        >
          <span>assigned to me</span>
          <span aria-hidden="true">{assignedToMe ? '✓' : ''}</span>
        </button>
      )}
      <div className="ez-person-filter__grid">
        {people.map((person) => (
          <button
            key={person.id}
            type="button"
            className="ez-person-filter__person"
            aria-pressed={picked.includes(person.id)}
            data-testid={`person-${person.id}`}
            onClick={() =>
              apply(
                picked.includes(person.id) ? picked.filter((id) => id !== person.id) : [...picked, person.id],
              )
            }
          >
            <PersonAvatar name={person.name} color={person.color} size={32} />
            <span>{person.name}</span>
          </button>
        ))}
      </div>
    </Pane>
  );
}
